import type { ApiError } from '@crv/contracts';
import { z } from 'zod';
import { getTokens } from '@crv/shared';

import { API_BASE_URL } from './base-url';
import { webRefreshTokenManager } from './auth-refresh-manager';
import { recordWebAuthMetric } from '../observability/auth-bootstrap-metrics';

const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  traceId: z.string().optional(),
  details: z.unknown().optional(),
});

function normalizeApiError(parsed: z.infer<typeof apiErrorSchema>): ApiError {
  return {
    code: parsed.code,
    message: parsed.message,
    traceId: parsed.traceId ?? 'unknown',
    details: parsed.details,
  };
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly body?: ApiError;

  constructor(message: string, status: number, body?: ApiError) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.body = body;
  }
}

const apiBaseUrl = API_BASE_URL;

export async function apiGet<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  return request(path, schema, { method: 'GET' });
}

export async function apiPost<T>(path: string, schema: z.ZodType<T>, body: unknown): Promise<T> {
  return request(path, schema, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function apiPut<T>(path: string, schema: z.ZodType<T>, body: unknown): Promise<T> {
  return request(path, schema, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function apiPatch<T>(path: string, schema: z.ZodType<T>, body: unknown): Promise<T> {
  return request(path, schema, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function apiDelete<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  return request(path, schema, { method: 'DELETE' });
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit,
): Promise<T> {
  if (shouldSkipAutoRefresh(path)) {
    return requestAttempt(path, schema, init);
  }

  return webRefreshTokenManager.withAutoRefresh(() => requestAttempt(path, schema, init));
}

async function requestAttempt<T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit,
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const metricPath = resolvePathname(path);
  const startMs = nowMs();
  const tokens = getTokens();
  const authHeader = tokens && tokens.accessToken ? `Bearer ${tokens.accessToken}` : undefined;

  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });

  if (authHeader) {
    headers.set('Authorization', authHeader);
  }

  if (init.headers) {
    applyExtraHeaders(headers, init.headers);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      ...init,
      headers,
    });
  } catch (error) {
    recordWebAuthMetric({
      kind: 'http-request',
      at: new Date().toISOString(),
      method,
      path: metricPath,
      durationMs: roundDuration(nowMs() - startMs),
      success: false,
      note: 'network-error',
    });
    throw error;
  }

  const body = await parseResponseBody(response);
  recordWebAuthMetric({
    kind: 'http-request',
    at: new Date().toISOString(),
    method,
    path: metricPath,
    status: response.status,
    success: response.ok,
    durationMs: roundDuration(nowMs() - startMs),
  });

  if (!response.ok) {
    const parsedApiError = apiErrorSchema.safeParse(body);
    const normalized = parsedApiError.success ? normalizeApiError(parsedApiError.data) : undefined;
    const message =
      normalized?.message
        ? normalized.message
        : `Request failed with status ${String(response.status)}`;
    throw new ApiClientError(
      message,
      response.status,
      normalized,
    );
  }

  return schema.parse(body);
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return response.text();
  }

  try {
    return await response.json();
  } catch {
    throw new ApiClientError('Invalid JSON response from API', response.status);
  }
}

function applyExtraHeaders(target: Headers, source: unknown): void {
  if (source instanceof Headers) {
    source.forEach((value, key) => {
      target.set(key, value);
    });
    return;
  }

  if (Array.isArray(source)) {
    for (const entry of source) {
      if (!Array.isArray(entry) || entry.length !== 2) {
        continue;
      }
      const tuple = entry as unknown[];
      const key = tuple[0];
      const value = tuple[1];
      if (typeof key === 'string' && typeof value === 'string') {
        target.set(key, value);
      }
    }
    return;
  }

  if (typeof source === 'object' && source !== null) {
    for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
      if (typeof value === 'string') {
        target.set(key, value);
      }
    }
  }
}

function buildUrl(path: string): string {
  if (path.startsWith('http')) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${apiBaseUrl}${normalizedPath}`;
}

function shouldSkipAutoRefresh(path: string): boolean {
  const pathname = resolvePathname(path);
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return normalized === '/v1/auth/refresh';
}

function resolvePathname(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      return new URL(url).pathname;
    } catch {
      // fall through
    }
  }

  const cutIndex = url.search(/[?#]/);
  return cutIndex === -1 ? url : url.slice(0, cutIndex);
}

function nowMs(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
}

function roundDuration(value: number): number {
  return Math.round(value * 100) / 100;
}
